import { GearItem, GearOwner, GearTarget, Rarity, SalvageSnapshot, StatUse, affixOn, gearStatUse, gearToJson, raiseCap, restoreSalvaged, salvageYield, sameOwner } from "@highmarch/domain";
import { GearCompare, OwnerView, bestLoadout, compareOn, itemScore } from "@highmarch/app";
import { RARITY_LABEL, fmtStat, pct, useNote } from "../gearUi";
import { motionOk } from "../kit";
import { toast } from "../toast";
import type { ArmouryCtx } from "./host";
import { tradeSellRow } from "../hub/marketSell";
import { btn, faceEl, fullTitle, gainEl, gainWords, gearDisc, h, helps, ico, isNew, ownerLevel, ownerName, ownerViews, paysFor, primaryAfterPlus, refusal, signed, signedPct, slotTitle, statShort, tag, tagIcon } from "./itemBits";
import { equipItem, offerUndo } from "./itemActions";
import { levelUp } from "./itemFx";
import { ForgeHooks, forgeSection } from "./itemForge";
import { openFusion } from "./fusion";
import { openFeed } from "./feed";
import { Panel, closeButton, closePanels, mountPanel, onRefresh, refreshPanels, swipeToClose } from "./itemPanels";

/**
 * Item detail (mockup3): the item big on its rarity glow, its numbers, and what to do with it — "Where it helps"
 * (only owners it fits and helps: the level's line-up first, each with what it wears now, the delta and EQUIP/SWAP;
 * bench owners folded into one row; owners too low shown with the way to Towers), the Forge (gear v2, itemForge.ts:
 * item level against its cap, Raise / Raise max for Forge Essence, fusion readiness and Feed), Lock and Unequip, and
 * the ways out — Salvage (its yield on the button, one confirm, then Undo) and the Trading Post (its Marks price).
 */

export interface Placement {
  readonly view: OwnerView;
  readonly cmp: GearCompare;
  /** "Equip best" for the next level puts it here. */
  readonly pick: boolean;
}
export interface CardModel {
  /** The owner wearing it now (if any). */
  readonly wearing: Placement | null;
  /** Line-up owners it fits and helps: the plan's pick first, then by gear gained. */
  readonly lineup: readonly Placement[];
  /** Bench owners it fits and helps, by gear gained. */
  readonly bench: readonly Placement[];
  /** Owners it would help that are below its level requirement. */
  readonly locked: readonly Placement[];
}

/** Where an item fits and helps (pure over the profile). */
export function cardModel(ctx: ArmouryCtx, item: GearItem): CardModel {
  const profile = ctx.profile, B = ctx.B;
  const plan = bestLoadout(profile, ctx.scope);
  const pickTo = plan.moves.find((m) => m.item.id === item.id && m.to !== null)?.to ?? null;
  const kind = B.gear.slots[item.slot].target;
  let wearing: Placement | null = null;
  const lineup: Placement[] = [], bench: Placement[] = [], locked: Placement[] = [];
  for (const view of ownerViews(ctx)) {
    if (!view.owned || view.owner.target !== kind) continue;
    const cmp = compareOn(profile, item, view.owner);
    const p: Placement = { view, cmp, pick: pickTo !== null && sameOwner(pickTo, view.owner) };
    if (cmp.wearing) wearing = p;
    else if (cmp.needLevel !== null) { if (helps(cmp)) locked.push(p); }
    else if (cmp.canWear === true && helps(cmp)) (view.seat ? lineup : bench).push(p);
  }
  const byGain = (a: Placement, b: Placement) => Number(b.pick) - Number(a.pick) || b.cmp.power - a.cmp.power || (a.view.seatRank ?? 0) - (b.view.seatRank ?? 0);
  return { wearing, lineup: lineup.sort(byGain), bench: bench.sort(byGain), locked: locked.sort((a, b) => a.cmp.needLevel! - b.cmp.needLevel! || b.cmp.power - a.cmp.power) };
}

/** How long the salvaged disc takes to shrink away before the card closes. */
const SALVAGE_OUT_MS = 260;

export function openItemCard(ctx: ArmouryCtx, item: GearItem, context?: { readonly owner?: GearOwner }): void {
  const B = ctx.B, profile = ctx.profile;
  const panel: Panel = mountPanel(ctx, "acard");
  const root = panel.root;
  let benchOpen = false;
  let confirming = false;

  const render = () => {
    const it = profile.findGear(item.id);
    if (it === null) { panel.close(); return; }
    const m = cardModel(ctx, it);
    const slot = B.gear.slots[it.slot];
    const wornOn = it.equippedTo;
    const focus = wornOn ?? context?.owner ?? null;
    root.className = `apanel acard r${it.rarity}`;

    const rib = h("div", "rib", h("span", "disp", RARITY_LABEL[it.rarity].toUpperCase()));
    if (isNew(ctx, it)) rib.prepend(tag("NEW", "new"));

    // Head: the icon, the name, the one big number.
    const disc = gearDisc(B, it);
    const meta = [`${slot.target === GearTarget.Tower ? "Tower" : "Fortress"} slot`, `Lv ${it.level} of ${Math.max(it.level, raiseCap(profile, it))}`, `⚡${itemScore(B, it)}`];
    const prim = h("div", "prim", h("span", "v disp", pct(primaryValueOn(it, null))), h("small", "", ` ${fmtStat(it.primaryStat)}`));
    if (focus !== null) {
      const c = compareOn(profile, it, focus);
      // on a damageless tower a stat it cannot use counts as its loved one (gear.json retarget): say which
      const as = c.primary.stat !== it.primaryStat ? ` ${statShort(c.primary.stat)}` : "";
      if (c.primary.affinity) prim.append(h("small", "aff", ` · ${pct(c.primary.after)}${as} on ${ownerName(B, focus)} ★`));
      const note = useNote(gearStatUse(B, focus, c.primary.stat), ownerName(B, focus));
      if (note) prim.append(h("small", "off", note));
    }
    const head = h("div", "dhead", disc, h("div", "dt", h("b", "nm disp", `${RARITY_LABEL[it.rarity]} ${slotTitle(B, it)}`), h("div", "meta", meta.join(" · ")), prim));

    // Chips: affixes, set piece, Mythic (dead here says so), locked / bound.
    const chips = h("div", "affx");
    // an affix the owner never uses (a tower's HP, a totem's fire rate) is struck through
    for (const raw of it.affixes) {
      const a = focus !== null ? affixOn(B, raw, focus) : raw;   // as it counts on the owner shown (retargeted on a totem)
      chips.append(h("span", focus !== null && gearStatUse(B, focus, a.stat) === StatUse.None ? "chip off" : "chip", `${pct(a.value)} ${fmtStat(a.stat)}`));
    }
    if (it.rarity >= B.gear.setMinRarity) chips.append(h("span", "chip set", "◆ Epic set piece"));
    if (it.locked) chips.append(tagIcon("lock", "Locked", "chip lockd"));
    if (it.bound) { const b = h("span", "chip", "Bound"); b.title = "Bound to your fortress: it can't be traded"; chips.append(b); }
    const mythic = it.mythicEffect >= 0 ? B.gear.mythicEffects[it.mythicEffect] : undefined;
    let mythicEl: HTMLElement | null = null;
    if (mythic !== undefined) {
      const active = focus === null ? null : compareOn(profile, it, focus).mythic?.active ?? false;
      // The name, the description and "does nothing on …" are one run of text: `.amyth` is a flex row (icon, text), so
      // bare text nodes beside the <b> each became a narrow column of their own.
      const text = h("span", "t", h("b", "", mythic.name), ` ${mythic.description}`);
      if (active === false) text.append(h("em", "", ` · does nothing on ${ownerName(B, focus!)}`));
      mythicEl = h("div", `amyth ${active === false ? "off" : "on"}`, ico("star", 13), text);
    }

    swipeToClose(panel, rib);
    // A worn item's next step is to raise it (the Forge first); a loose one's is where to put it (Where it helps first).
    const forge = forgeSection(ctx, it, forgeHooks), helpsEl = whereItHelps(it, m);
    const middle = it.equippedTo !== null ? [forge, helpsEl] : [helpsEl, forge];
    root.replaceChildren(rib, closeButton(panel.close), h("div", "dbody", head, chips, mythicEl, ...middle, footer(it), footNote(it)));
  };

  const primaryValueOn = (it: GearItem, owner: GearOwner | null) => primaryAfterPlus(B, it, owner).now;
  /** The Forge section's ways out: redraw here, the Fusion screen on this keeper, the Feed panel on this target. */
  const forgeHooks: ForgeHooks = {
    root,
    after: () => after(),
    openFusion: (k) => openFusion(ctx, { keeper: k, openItem: (it) => openItemCard(ctx, it) }),
    openFeed: (t) => openFeed(ctx, t, (r) => {
      after();
      const disc = root.querySelector<HTMLElement>(".adisc");
      if (disc !== null && r.to > r.from) levelUp(disc, r.to - r.from > 1 ? `LV ${r.from} → ${r.to}` : `LV ${r.to}`, t.rarity);
    }),
  };

  /** Owners it fits and helps, best first; what each wears now; one tap to put it there. */
  function whereItHelps(it: GearItem, m: CardModel): HTMLElement {
    const sec = h("div", "dsec", h("div", "gh", ico("tower", 12), "Where it helps", h("span", "ln")));
    if (m.wearing !== null) {
      const w = m.wearing, nm = ownerName(B, w.view.owner);
      const off = btn("abtn parch sm", h("span", "disp", "Unequip"));
      off.addEventListener("click", () => {
        const r = profile.unequipGear(it.id);
        if (r !== true) { toast(ctx.toastParent, refusal(r)); return; }
        ctx.changed(); after();
      });
      sec.append(h("div", "orow worn", faceEl(B, w.view.owner), h("div", "mid", h("b", "", `On ${nm} now`),
        h("small", "", `gives ${pct(w.cmp.primary.after)} ${statShort(it.primaryStat)}${w.cmp.primary.affinity ? " ★" : ""} · ⚡${itemScore(B, it)}`)), off));
    }
    for (const p of m.lineup) sec.append(ownerRow(it, p));
    if (m.bench.length > 0) {
      if (benchOpen) for (const p of m.bench) sec.append(ownerRow(it, p));
      else {
        const more = btn("amore", `${m.bench.length} bench ${m.bench.length === 1 ? "tower" : "towers"}`, ico("chev", 14));
        more.addEventListener("click", () => { benchOpen = true; render(); });
        sec.append(more);
      }
    }
    for (const p of m.locked) {
      const nm = ownerName(B, p.view.owner);
      const go = btn("abtn blue sm", h("span", "disp", "Upgrade"));
      go.addEventListener("click", () => ctx.openTowers());
      sec.append(h("div", "orow lockrow", faceEl(B, p.view.owner), h("div", "mid", h("b", "", nm, tagIcon("lock", `Lv ${p.cmp.needLevel}`, "lock")),
        h("small", "", `${nm} is Lv ${ownerLevel(profile, p.view.owner)} · would gain ⚡${signed(p.cmp.power)}`)), go));
    }
    if (m.wearing === null && m.lineup.length === 0 && m.bench.length === 0 && m.locked.length === 0) {
      sec.append(h("p", "nohelp", "Every tower wears something as good"));
    }
    return sec;
  }

  function ownerRow(it: GearItem, p: Placement): HTMLElement {
    const c = p.cmp, nm = ownerName(B, p.view.owner);
    const lvl = ctx.scope.level;
    const title = h("b", "", nm);
    if (p.pick) title.append(tagIcon("flag", lvl !== undefined ? `L${lvl} pick` : "Best pick", "pick"));
    const what = c.worn === null ? `${B.gear.slots[it.slot].name} socket is empty` : `Swaps out ${RARITY_LABEL[c.worn.rarity]} ${slotTitle(B, c.worn)}`;
    const from = c.takesFrom !== null ? ` · takes it off ${ownerName(B, c.takesFrom)}` : "";
    const d = c.primary.after.sub(c.primary.before);
    const delta = h("span", "d", `${signedPct(d)} ${statShort(c.primary.stat)}${c.primary.affinity ? " ★" : ""} · ⚡${signed(c.power)}`);
    const go = btn(`abtn ${p.pick ? "gold" : "blue"} sm`, h("span", "disp", c.worn === null ? "Equip" : "Swap"));
    go.addEventListener("click", () => {
      if (!equipItem(ctx, it, p.view.owner, c.mythic?.active === true)) return;
      closePanels(ctx);
      ctx.focusOwner(p.view.owner);
    });
    const row = h("div", `orow${p.pick ? " best" : ""}`, faceEl(B, p.view.owner), h("div", "mid", title, h("small", "", what + from), delta), go);
    row.dataset.owner = p.view.id;
    return row;
  }

  /**
   * Lock and Unequip, then "Done with it?" (M30 #8, Kevin: "hold to salvage and the trading post button don't make
   * sense"): each way out is one button that says what it does and what it pays — "Salvage for +120 gold, +40
   * essence" (a tap asks once, then an Undo toast) and "Sell at the Trading Post for 1,200 Marks" — over a line on
   * what those currencies are for. Locked can't; worn says what its tower loses. (#24: Unequip takes it off without a
   * swap; it goes back to the stash.)
   */
  function footer(it: GearItem): HTMLElement {
    const lock = btn(`abtn parch lockb${it.locked ? " on" : ""}`, ico("lock", 18), h("span", "", it.locked ? "Locked" : "Lock"));
    lock.setAttribute("aria-pressed", String(it.locked));
    lock.addEventListener("click", () => {
      const r = profile.setGearLock(it.id, !it.locked);
      if (r !== true) { toast(ctx.toastParent, refusal(r)); return; }
      ctx.changed(); after();
    });
    const row = h("div", "dfoot", lock);
    if (it.equippedTo !== null) {
      const on = ownerName(B, it.equippedTo);
      const off = btn("abtn blue unequip", h("span", "disp", "Unequip"), h("small", "", `Back to the stash · ${on} loses ⚡${itemScore(B, it)}`));
      off.setAttribute("aria-label", `Unequip from ${on}: back to the stash`);
      off.addEventListener("click", () => unequip(it));
      row.append(off);
    }
    const y = salvageYield(B, it);
    const salv = btn(`abtn parch salvgo${it.locked ? " off" : ""}`, h("span", "disp", "Salvage for"), gainEl(y.gold, "again g", y.essence));
    salv.setAttribute("aria-label", it.locked ? "Locked: unlock it to salvage" : `Salvage for ${gainWords(y.gold, y.essence)}`);
    if (it.locked) salv.setAttribute("aria-disabled", "true");
    salv.addEventListener("click", () => {
      if (it.locked) { toast(ctx.toastParent, "It's locked: tap Lock to unlock it first"); return; }
      confirmSalvage(it);
    });
    const why = it.locked ? h("small", "dwhy", "Locked: unlock it to salvage or sell") : it.equippedTo !== null ? h("small", "dwhy warn", `Worn by ${ownerName(B, it.equippedTo)}: salvaging takes it off`) : null;
    const sell = tradeSellRow(it, ctx.toastParent, () => { ctx.changed(); refreshPanels(ctx, root); panel.close(); });
    return h("div", "dout", row, h("div", "gh", ico("bag", 12), "Done with it?", h("span", "ln")), salv, why, sell);
  }

  /** Takes it off its owner (no swap): it goes back to the stash, and the card stays on it. */
  function unequip(it: GearItem): void {
    const on = it.equippedTo === null ? "" : ownerName(B, it.equippedTo);
    const r = profile.unequipGear(it.id);
    if (r !== true) { toast(ctx.toastParent, refusal(r)); return; }
    ctx.changed(); after();
    toast(ctx.toastParent, `${fullTitle(B, it)} is back in the stash${on ? `: ${on}'s socket is empty` : ""}`);
  }

  /** One explicit step before it goes (a Mythic also names the effect lost); then the Undo toast. */
  function confirmSalvage(it: GearItem): void {
    if (confirming) return;
    confirming = true;
    const y = salvageYield(B, it);
    const def = it.rarity === Rarity.Mythic ? B.gear.mythicEffects[it.mythicEffect] : undefined;
    const keep = btn("abtn parch", h("span", "disp", "Keep it"));
    const go = btn("abtn dark", h("span", "disp", "Salvage"));
    const lose = def !== undefined ? `${def.name} will be gone for good. ` : it.equippedTo !== null ? `${ownerName(B, it.equippedTo)} loses it. ` : "";
    const box = h("div", "aconfirm", h("b", "disp", `Salvage ${fullTitle(B, it)}?`), h("p", "", `${lose}You get `, gainEl(y.gold, "again", y.essence), "."), h("div", "acts", keep, go));
    keep.addEventListener("click", () => { confirming = false; box.remove(); render(); });
    go.addEventListener("click", () => { confirming = false; box.remove(); salvage(it); });
    root.append(box);
  }

  function footNote(it: GearItem): HTMLElement | null {
    const lines: HTMLElement[] = [];
    if (!it.locked) {
      const pays = paysFor(ctx, salvageYield(B, it).essence, new Set([it.id]));
      if (pays !== null) lines.push(h("div", "pays", ico("bolt", 12), `Salvage ${pays}`));
    }
    return lines.length > 0 ? h("div", "dnote", ...lines) : null;
  }

  function salvage(it: GearItem): void {
    const wasOn = it.equippedTo === null ? null : { ...it.equippedTo };
    const json = gearToJson(it);
    const title = fullTitle(B, it);
    const v = profile.salvageGear(it.id, true);
    if (typeof v === "string") { toast(ctx.toastParent, refusal(v)); render(); return; }
    const snap: SalvageSnapshot = { items: [json], value: v.gold, essence: v.essence };
    ctx.changed();
    refreshPanels(ctx, root);
    offerUndo(ctx, `Salvaged ${title}: ${gainWords(v.gold, v.essence)}`, () => {
      const r = restoreSalvaged(profile, snap);
      if (r !== true) { toast(ctx.toastParent, refusal(r)); return; }
      // Back where it was when that socket is still free (Undo is placement-only; binding never changes here).
      if (wasOn !== null && profile.equippedIn(wasOn, json.slot) === null) profile.equipGear(json.id, wasOn);
      ctx.changed();
      refreshPanels(ctx);
    });
    const discEl = root.querySelector<HTMLElement>(".adisc");
    if (discEl !== null && typeof discEl.animate === "function" && motionOk()) {
      discEl.animate([{ transform: "scale(1)", opacity: 1 }, { transform: "scale(0.2)", opacity: 0 }], { duration: SALVAGE_OUT_MS, easing: "ease-in" }).onfinish = () => panel.close();
    } else panel.close();
  }

  /** After a change made here: the page re-rendered (ctx.changed), keep this card on it and redraw. */
  function after(): void {
    panel.keep();
    render();
    refreshPanels(ctx, root);
  }

  onRefresh(panel, render);
  render();
}
